import * as ngCore from '@angular/core'
import * as ngMatCore from '@angular/material/core'
import * as ds from '@attain-apps/fe/web/main/lib/design'

import * as number from '../../../../../number'
import * as select from '../../../../../select'
import * as models from '../../../../models'
import { Store } from '../../../../store'

@ngCore.Component({
  encapsulation: ngCore.ViewEncapsulation.None,
  imports: [number.Component, select.single.Component],
  selector: 'atn-ds-date-range-duration-input',
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component {
  /** @see {@link Store} */
  public readonly store = ngCore.inject(Store)

  /** @see {@link ngMatCore.DateAdapter} */
  private readonly _dateAdapter = ngCore.inject<ngMatCore.DateAdapter<Date>>(
    ngMatCore.DateAdapter,
  )

  /**
   * Converts {@link models.DateRange.Duration.Unit} enum into an array of
   * {@link ds.input.select.models.Item} for use in the select dropdown
   */
  public readonly durationUnits: ds.input.select.models.Item[] =
    models.DateRange.Duration.Unit.ALL.map(label => ({
      label,
      value: label,
    }))

  /** @see {@link models.DateRange} */
  private readonly _editingDateRangeForComponentSg =
    ngCore.signal<models.DateRange>(models.DateRange.generate())

  /** @see {@link models.DateRange.Duration} */
  private readonly _durationSg = ngCore.linkedSignal<models.DateRange.Duration>(
    (editingDateRange = this._editingDateRangeForComponentSg()) => {
      const duration = models.DateRange.Duration.calculate(
        { adapter: this._dateAdapter },
        { range: editingDateRange },
      )
      if (!duration) {
        return { length: undefined, unit: models.DateRange.Duration.Unit.Years }
      }

      return duration
    },
  )

  /** @see {@link models.DateRange.Duration.length} */
  public readonly durationLengthSg = ngCore.computed(
    (duration = this._durationSg()) => duration.length,
  )

  /** @see {@link models.DateRange.Duration.Unit} */
  public readonly durationUnitSg = ngCore.computed(
    (duration = this._durationSg()) => duration.unit,
  )

  constructor() {
    ngCore.effect(() => {
      const editingDateRange = this.store.editingDateRangeSg()

      // NOTE: The update is delayed using `setTimeout` to let Angular complete its
      // change detection cycle first. This ensures the input element correctly
      // reflects the signal-controlled value, particularly when the user's raw input
      // (e.g., `12 Months`) is immediately normalized to a different value (e.g., `1 Year`),
      // maintaining consistent UI behavior.
      setTimeout(() => {
        this._editingDateRangeForComponentSg.set(editingDateRange)
      })
    })
  }

  /**
   *  Updates the date range when the duration unit is changed
   * (e.g., from months to weeks).
   */
  public durationUnitChangeHandler(unit: unknown) {
    if (!models.DateRange.Duration.Unit.isUnit(unit)) {
      return
    }

    this._durationSg.update(current => ({ ...current, unit }))
    this._recalculateDateRange()
  }

  /**
   * Updates the date range when the duration length changes.
   */
  public durationLengthChangeHandler(length?: number) {
    this._durationSg.update(current => ({ ...current, length }))

    this._recalculateDateRange()
  }

  /**
   * Recalculates the date range based on the current duration
   */
  // TODO Try to move this logic into an effect
  private _recalculateDateRange(): void {
    const updatedRange = models.DateRange.calculate(
      { adapter: this._dateAdapter },
      {
        currentDateRange: this._editingDateRangeForComponentSg(),
        duration: this._durationSg(),
        lastChanged: this.store.lastChangedSg(),
      },
    )

    this.store.patchState({ editingDateRange: updatedRange })
  }
}
